import { DateTime, IANAZone } from "luxon";
import { Nullable } from "sonobello.utilities.react";

import EnvironmentConfiguration from "../types/EnvironmentConfiguration";
import { ContactResult } from "../types/ContactResult";
import { DisqualifyingReason } from "../types/DisqualifyingReason";
import { EnhancedToken, Token } from "../types/Token";
import {
  LocalStorageConfigs,
  loadFromLocal,
  loadFromLocalRaw,
  loadFromLocalRawWithFallback,
  loadFromLocalWithFallback,
  saveToLocal
} from "../types/LocalStorage";
import IMedicalConclusions, { NullMedicalConclusions } from "../steps/Medical/types/IMedicalConclusions";
import IMedicalQualification from "../steps/Medical/types/IMedicalQualification";
import { IPatient, Patient } from "./Entities/Patient";
import { ICached } from "./ICached";
import ICenter, { Center } from "./ICenter";
import IReservation from "./IReservation";
import { CachedReservation, ICachedReservation } from "./Reservation";

/** The shape of a patient record as persisted in the browser cache (current domain terms). */
interface ICachedPatient {
  centerId: string;
  dateOfBirth?: string;
  distanceToCenter?: number;
  id: string;
  email?: string;
  firstName: string;
  height?: { inches: number; feet: number };
  lastName: string;
  lead: {
    id: string;
    campaign: { businessUnit: { id: string; name: string } };
  };
  phoneNumber?: string;
  weight?: number;
  zipCode?: string;
  isPreviouslyQualified: boolean;
  qualificationDateUtc?: string;
}

/** Legacy shape of a cached patient record, written before the `opportunity` → `lead` domain rename. */
interface ILegacyCachedPatient {
  centerId: string;
  dateOfBirth?: string;
  distanceToCenter?: number;
  id: string;
  email?: string;
  firstName: string;
  height?: { inches: number; feet: number };
  lastName: string;
  opportunity: {
    id: string;
    campaign: { businessUnit: { id: string; name: string } };
  };
  phoneNumber?: string;
  weight?: number;
  zipCode?: string;
  isPreviouslyQualified: boolean;
  qualificationDateUtc?: string;
}

const isLegacyCachedPatient = (p: ICachedPatient | ILegacyCachedPatient): p is ILegacyCachedPatient =>
  "opportunity" in p;

/** A representation of all of the values necessary to run an instance of the application. */
interface ISession {
  /** The patient associated with the current OBX session. */
  readonly patient: IPatient;
  /** The center associated with the current OBX session. */
  readonly defaultCenter: DefaultCenter;
  /** The reasons why a user can be considered a non-candidate. */
  readonly disqualifyingReasons?: DisqualifyingReason[];
  /** The key for the current OBX session taken from the unique OBX url. */
  readonly key: string;
  /** The name of the Flow which the user should experience in this session. */
  readonly flowName: string;
  /** Flag indicating if the device for this local session has been recorded. */
  readonly isDeviceLogged: boolean;
  /** The id of the currently active contact for this OBX session. */
  readonly contactId: string;
  /** The result for the OBX session. */
  readonly contactResult: Nullable<ContactResult>;
  /** The id of the currently active flow for this OBX session. */
  readonly flowId: string;
  /** The medical conclusions for the user. */
  readonly medicalConclusions: IMedicalConclusions;
  /** The state of the medical form for this OBX session. */
  readonly medicalForm: Nullable<IMedicalQualification>;
  /** The appointment reservation currently held by the session. */
  readonly reservation: Nullable<IReservation>;
  /** The bearer authorization token used to make requests for this OBX session. */
  readonly token: EnhancedToken;
  /** The version number of the software that is compatible with this session. */
  readonly version?: string;
}

/** {@inheritdoc ISession} */
export class Session implements ISession {
  readonly defaultCenter: DefaultCenter;
  readonly patient: IPatient;
  readonly disqualifyingReasons?: DisqualifyingReason[];
  readonly key: string;
  readonly flowName: string;
  readonly isDeviceLogged: boolean;
  readonly contactId: string;
  readonly contactResult: Nullable<ContactResult>;
  readonly flowId: string;
  readonly medicalConclusions: IMedicalConclusions;
  readonly medicalForm: Nullable<IMedicalQualification>;
  readonly reservation: Nullable<IReservation>;
  readonly token: EnhancedToken;
  readonly version?: string;

  constructor(
    patient: IPatient,
    defaultCenter: DefaultCenter,
    key: string,
    flowName: string,
    isDeviceLogged: boolean,
    contactId: string,
    flowId: string,
    token: EnhancedToken,
    version: string,
    contactResult: Nullable<ContactResult>,
    medicalConclusions: Nullable<IMedicalConclusions>,
    medicalForm: Nullable<IMedicalQualification>,
    reservation: Nullable<IReservation>,
    disqualifyingReasons?: DisqualifyingReason[]
  ) {
    this.defaultCenter = defaultCenter;
    this.patient = patient;
    this.disqualifyingReasons = disqualifyingReasons;
    this.flowName = flowName;
    this.isDeviceLogged = isDeviceLogged;
    this.key = key;
    this.contactId = contactId;
    this.contactResult = contactResult;
    this.flowId = flowId;
    this.medicalConclusions = medicalConclusions || new NullMedicalConclusions();
    this.medicalForm = medicalForm;
    this.reservation = reservation;
    this.token = token;
    this.version = version;
  }
}

/** The default center to be used for the session. */
export class DefaultCenter extends Center implements ICached {
  constructor(id: string, name: string, address: string, phoneNumber: string, timeZone: IANAZone) {
    super(id, null, name, address, phoneNumber, timeZone);
  }
  /** {@inheritdoc ICached.cache} */
  readonly cache = () => saveToLocal(this, LocalStorageConfigs.center.key);
}

/** A default center that has been restored from the browser cache. */
class CachedDefaultCenter extends DefaultCenter {
  constructor(center: ICenter) {
    super(center.id, center.name, center.address, center.phoneNumber, center.timeZone);
  }
}

/** A session constructed the content of the browser cache. */
export class CachedSession extends Session {
  /**
   * @throws if the session cannot be built from the cache.
   */
  constructor() {
    const defaultCenter = loadFromLocal<ICenter>(LocalStorageConfigs.center.key);
    const patientData = loadFromLocalWithFallback<ICachedPatient | ILegacyCachedPatient>(
      LocalStorageConfigs.patient.key,
      LocalStorageConfigs.legacy.patient.key
    );
    const token = loadFromLocal<Token>(LocalStorageConfigs.token.key);

    // build an unvalidated session
    const session = {
      patient:
        patientData &&
        new Patient(
          isLegacyCachedPatient(patientData)
            ? { ...patientData, lead: patientData.opportunity } // legacy cache failover: opportunity → lead
            : patientData
        ),
      defaultCenter: defaultCenter && new CachedDefaultCenter(defaultCenter),
      disqualifyingReasons: loadFromLocal<DisqualifyingReason[]>(LocalStorageConfigs.disqualifyingReasons.key),
      isDeviceLogged:
        Boolean(
          loadFromLocalRawWithFallback(
            LocalStorageConfigs.isDeviceLogged.key,
            LocalStorageConfigs.legacy.isDeviceLogged.key
          )
        ) || false,
      contactId: loadFromLocalRawWithFallback(
        LocalStorageConfigs.contactId.key,
        LocalStorageConfigs.legacy.contactId.key
      ),
      contactResult: loadFromLocalWithFallback<ContactResult>(
        LocalStorageConfigs.contactResult.key,
        LocalStorageConfigs.legacy.contactResult.key
      ),
      flowId: loadFromLocalRaw(LocalStorageConfigs.flowId.key),
      flowName: loadFromLocalRaw(LocalStorageConfigs.flow.key),
      key: loadFromLocalRaw(LocalStorageConfigs.sessionKey.key),
      medicalConclusions: loadFromLocal<IMedicalConclusions>(LocalStorageConfigs.medicalConclusions.key),
      medicalForm: loadFromLocal<IMedicalQualification>(LocalStorageConfigs.medicalForm.key),
      token: token && new EnhancedToken(token),
      version: loadFromLocalRaw(LocalStorageConfigs.version.key)
    };

    // return undefined if we cannot validate the session
    if (
      !session.patient ||
      !session.defaultCenter ||
      !session.key ||
      !session.flowName ||
      !session.contactId ||
      !session.flowId ||
      !session.token
    )
      throw "The session does not exist or is missing required properties.";

    // load optional properties
    let reservation: Nullable<IReservation> = null;
    const cachedReservation = loadFromLocalWithFallback<ICachedReservation>(
      LocalStorageConfigs.reservation.key,
      LocalStorageConfigs.legacy.reservation.key
    );
    if (cachedReservation) reservation = new CachedReservation(cachedReservation);
    if (reservation && reservation.expirationUtc < DateTime.utc()) reservation = null;
    super(
      session.patient,
      session.defaultCenter,
      session.key,
      session.flowName,
      session.isDeviceLogged,
      session.contactId,
      session.flowId,
      session.token,
      session.version || EnvironmentConfiguration.version,
      session.contactResult || null,
      session.medicalConclusions || null,
      session.medicalForm || null,
      reservation,
      session.disqualifyingReasons
    );
  }
}

export default ISession;
